import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import LegalFooter from "@/components/legal/LegalFooter";
import AuthAcknowledgement from "@/components/legal/AuthAcknowledgement";
import { HardHat, Lock, ChevronRight, ShieldCheck, ArrowLeft } from "lucide-react";

/**
 * Customer login portal — /login.
 *
 * Email/password only. No Google login (removed per public-site
 * separation directive). Self-service signup exists ONLY for the
 * Starter tier at /signup (Founder Commercial Amendment, 2026-09-07) —
 * every other tier remains sales-assisted, provisioned by a platform
 * admin.
 *
 * A subtle "ForgedOps Staff?" link routes to /platform, which redirects back
 * here when unauthenticated and surfaces the Operations Console hint. Platform
 * admin authority is gated by the PLATFORM_ADMIN_EMAILS env allowlist server-side.
 */
export default function Login() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Hard-Gate P1 (2026-09-06) — MFA challenge step (shown after a
  // password check succeeds for an ENROLLED privileged account).
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [mfaError, setMfaError] = useState("");

  // If a ProtectedRoute redirected us here, honour the intended destination
  // (so e.g. "Open Operations Console" → /platform routes back to /platform
  // after successful sign-in, instead of dumping platform admins on /portfolio).
  const fromPathname = location.state?.from?.pathname || "";
  const redirectTo = fromPathname || "/portfolio";
  const isOpsConsoleIntent = redirectTo === "/platform";

  // Contextual sign-in message — construction language, plain English.
  // Surfaced only when a ProtectedRoute bounced the user here from a real
  // destination. Mapping is intentionally narrow so each surface tells the
  // operator WHAT they were trying to reach, not WHY auth failed.
  const contextMessageFor = (path) => {
    if (!path) return null;
    if (path.startsWith("/sheet/")) return "Please sign in to view this story.";
    if (path.includes("/war-room")) return "Please sign in to view revision activity.";
    if (path.startsWith("/field-roll") || path.includes("/field-timeline")) return "Please sign in to view field activity.";
    if (path.startsWith("/quantity-intelligence")) return "Please sign in to view quantity information.";
    if (path.startsWith("/operations")) return "Please sign in to view operations activity.";
    if (path.startsWith("/inbox")) return "Please sign in to view your inbox.";
    if (path.startsWith("/command")) return "Please sign in to view command activity.";
    if (path.startsWith("/portfolio")) return "Please sign in to view your project portfolio.";
    if (path.startsWith("/project/") && path.includes("/revisions")) return "Please sign in to view revision activity.";
    if (path.startsWith("/project/") && path.includes("/plans")) return "Please sign in to view plan sheets.";
    if (path.startsWith("/project/")) return "Please sign in to view this project.";
    if (path.startsWith("/discoveries")) return "Please sign in to view discoveries.";
    if (path.startsWith("/intake")) return "Please sign in to continue intake.";
    if (path.startsWith("/digest")) return "Please sign in to view the daily digest.";
    if (path.startsWith("/issues")) return "Please sign in to view open issues.";
    if (path.startsWith("/knowledge")) return "Please sign in to view the knowledge log.";
    if (path.startsWith("/ask")) return "Please sign in to ask the field assistant.";
    if (path.startsWith("/workflow/")) return "Please sign in to view this workflow.";
    if (path.startsWith("/spec-section/")) return "Please sign in to view this spec section.";
    return "Please sign in to continue.";
  };
  const contextMessage = isOpsConsoleIntent ? null : contextMessageFor(fromPathname);

  const proceedAfterAuth = (user) => {
    // Iter EXP-2A — Resume last location.
    // The platform should feel continuous, not stateless. If the user
    // closed the iPad on a sheet and reopens 10 minutes (or 12 hours)
    // later, return them to that sheet — not to the portfolio
    // picker. Only honour resume-state if it was saved for THIS
    // user (key includes user id) AND it's not older than 7 days.
    const fromPath = location.state?.from?.pathname;
    let resumeTarget = null;
    try {
      if (!fromPath && !user?.is_platform_admin) {
        const key = `fop:last-location:${user?.id}`;
        const raw = window.localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          const age = Date.now() - (parsed.ts || 0);
          if (parsed.path && age < 7 * 24 * 60 * 60 * 1000) {
            resumeTarget = { path: parsed.path, label: parsed.label || null };
          }
        }
      }
    } catch { /* corrupt — fall through to default */ }
    const target = fromPath
      || resumeTarget?.path
      || (user?.is_platform_admin ? "/platform" : "/portfolio");
    // Stash the human label so the destination page can show a
    // "Welcome back — resuming EC-100" toast on next render.
    if (resumeTarget?.label) {
      try { window.sessionStorage.setItem("fop:resume-toast", resumeTarget.label); } catch { /* noop */ }
    }
    navigate(target, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result?.mfaRequired) {
        setMfaToken(result.mfaToken);
        return;
      }
      proceedAfterAuth(result);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setMfaError("");
    setSubmitting(true);
    try {
      const user = await verifyMfa(mfaToken, mfaCode.trim());
      proceedAfterAuth(user);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setMfaError(typeof d === "string" ? d : "Incorrect code");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
    <div className="flex-1 grid lg:grid-cols-[1fr_520px]">
      {/* Hero side */}
      <div className="hidden lg:flex relative overflow-hidden bg-[var(--fop-surface)] border-r border-[var(--fop-border)]">
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full" style={{
          background: "radial-gradient(circle, var(--fop-primary-soft), transparent 70%)",
        }} />
        <div className="absolute bottom-0 left-0 right-0 h-2 industrial-stripe" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link to="/" className="inline-flex" data-testid="login-back-home">
            <Logo size="lg" />
          </Link>

          <div className="max-w-md">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--fop-primary)] mb-4">
              Forgedops Plans™
            </div>
            <h1 className="font-display text-5xl text-[var(--fop-text)] leading-[1.05] mb-3">
              Operational Plan Room.
            </h1>
            <div className="text-[var(--fop-text-2)] text-sm font-mono uppercase tracking-[0.2em] mb-7">
              Heavy Civil · DOT · FAA · Utilities · Earthwork
            </div>

            <div className="space-y-1.5">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)] mb-3">
                Capabilities
              </div>
              {[
                "Current plan sets & revision history",
                "Per-sheet measurements & calibrated scale",
                "GPS positioning on calibrated drawings",
                "Utility intelligence — crossings, clearances, hotspots",
                "Field acknowledgments tied to revisions",
                "Offline field access — drawings, photos, measurements",
              ].map((cap) => (
                <div key={cap} className="flex items-start gap-2.5 text-sm text-[var(--fop-text-2)]">
                  <span className="font-mono text-[var(--fop-primary)] mt-0.5 text-xs leading-none">▸</span>
                  <span>{cap}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-4)] font-mono flex items-center gap-3">
            <span>FORGEDOPS PLANS™</span>
            <span>·</span>
            <span>AUTHORIZED ACCESS ONLY</span>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex flex-col items-center justify-center p-6 landscape:p-4 sm:p-8 lg:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-4 landscape:mb-2 flex items-center justify-between">
            <Link to="/" data-testid="login-back-home-mobile"><Logo /></Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
            >
              <ArrowLeft size={12} /> Home
            </Link>
          </div>

          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--fop-text-2)] mb-2 landscape:hidden">
            Field Coordination
          </div>
          <h2 className="font-display text-3xl sm:text-4xl landscape:text-2xl text-[var(--fop-text)] mb-1.5">
            {isOpsConsoleIntent ? "Operations Console" : "Sign in"}
          </h2>
          <p className="text-base text-[var(--fop-text-2)] mb-5 landscape:mb-3 landscape:text-sm">
            {isOpsConsoleIntent
              ? "ForgedOps staff only. Sign in to access the Operations Console."
              : "Authorized field operations access."}
          </p>

          {isOpsConsoleIntent && (
            <div className="mb-6 flex items-start gap-2.5 rounded-md border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] px-3.5 py-3 text-sm text-[var(--fop-text-2)]" data-testid="ops-console-hint">
              <ShieldCheck size={16} className="text-[var(--fop-primary)] mt-0.5 shrink-0" />
              <div>
                You&apos;ll be routed to the ForgedOps Operations Console after sign-in.
                Customer staff should <Link to="/login" replace className="text-[var(--fop-primary)] underline underline-offset-2 hover:no-underline">use the standard sign-in</Link> instead.
              </div>
            </div>
          )}

          {contextMessage && (
            <div
              className="mb-6 flex items-start gap-2.5 rounded-md border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] px-3.5 py-3 text-sm text-[var(--fop-text-2)]"
              data-testid="login-context-message"
            >
              <ShieldCheck size={16} className="text-[var(--fop-primary)] mt-0.5 shrink-0" />
              <div>{contextMessage}</div>
            </div>
          )}

          {!mfaToken ? (
          <form onSubmit={submit} className="space-y-5 landscape:space-y-3" data-testid="login-form">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-2)] mb-2 block">
                Email
              </label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-2)] mb-2 block">
                Password
              </label>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                placeholder="••••••••"
              />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" data-testid="login-forgot-password-link"
                       className="text-xs text-[var(--fop-text-3)] hover:text-[var(--fop-primary)] underline underline-offset-2">
                  Forgot password?
                </Link>
              </div>
            </div>
            {error && (
              <div className="text-[var(--fop-danger)] text-sm font-mono px-3 py-2.5 rounded border border-[rgba(220,38,38,0.35)] bg-[rgba(220,38,38,0.08)]" data-testid="login-error">
                {error}
              </div>
            )}
            <button type="submit" disabled={submitting}
                     className="btn btn-primary w-full mt-2"
                     style={{ minHeight: "48px", fontSize: "16px" }}
                     data-testid="login-submit">
              <Lock size={16} />
              {submitting ? "Signing in…" : "Sign in"}
              {!submitting && <ChevronRight size={16} className="ml-auto" />}
            </button>
          </form>
          ) : (
          <form onSubmit={submitMfa} className="space-y-5" data-testid="mfa-challenge-form">
            <div className="mb-2 flex items-start gap-2.5 rounded-md border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] px-3.5 py-3 text-sm text-[var(--fop-text-2)]" data-testid="mfa-challenge-hint">
              <ShieldCheck size={16} className="text-[var(--fop-primary)] mt-0.5 shrink-0" />
              <div>
                {useRecovery
                  ? "Enter one of your 10 recovery codes."
                  : "Enter the 6-digit code from your authenticator app."}
              </div>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-2)] mb-2 block">
                {useRecovery ? "Recovery code" : "Authentication code"}
              </label>
              <input
                type="text" required value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                data-testid="mfa-code-input"
                placeholder={useRecovery ? "XXXX-XXXX-XXXX" : "123456"}
                autoFocus
                inputMode={useRecovery ? "text" : "numeric"}
              />
            </div>
            {mfaError && (
              <div className="text-[var(--fop-danger)] text-sm font-mono px-3 py-2.5 rounded border border-[rgba(220,38,38,0.35)] bg-[rgba(220,38,38,0.08)]" data-testid="mfa-error">
                {mfaError}
              </div>
            )}
            <button type="submit" disabled={submitting}
                     className="btn btn-primary w-full mt-2"
                     style={{ minHeight: "48px", fontSize: "16px" }}
                     data-testid="mfa-verify-submit">
              <ShieldCheck size={16} />
              {submitting ? "Verifying…" : "Verify"}
            </button>
            <div className="flex items-center justify-between text-xs">
              <button type="button"
                      onClick={() => { setUseRecovery((v) => !v); setMfaCode(""); setMfaError(""); }}
                      className="text-[var(--fop-text-3)] hover:text-[var(--fop-primary)] underline underline-offset-2"
                      data-testid="mfa-toggle-recovery">
                {useRecovery ? "Use authenticator code instead" : "Use a recovery code instead"}
              </button>
              <button type="button"
                      onClick={() => { setMfaToken(null); setMfaCode(""); setMfaError(""); setPassword(""); }}
                      className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] inline-flex items-center gap-1"
                      data-testid="mfa-back-to-login">
                <ArrowLeft size={11} /> Back
              </button>
            </div>
          </form>
          )}

          <div className="mt-5 text-sm text-[var(--fop-text-3)] text-center" data-testid="login-help">
            Need access? Contact your company administrator.
            <span className="block mt-1">
              Starting a new company?{" "}
              <Link to="/signup" className="text-[var(--fop-primary)] underline underline-offset-2 hover:no-underline" data-testid="login-signup-link">
                Sign up for Starter
              </Link>
            </span>
          </div>

          <AuthAcknowledgement testid="login-acknowledgement" action="signing in to" />

          <div className="mt-8 pt-5 border-t border-[var(--fop-border)] flex items-center justify-between text-xs font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)]">
            <span>FORGEDOPS PLANS™</span>
            {!isOpsConsoleIntent ? (
              <Link
                to="/platform"
                className="inline-flex items-center gap-1.5 text-[var(--fop-text-3)] hover:text-[var(--fop-primary)] transition-colors"
                data-testid="ops-console-link"
                title="ForgedOps staff sign-in — for onboarding/support operators"
              >
                <HardHat size={12} />
                ForgedOps staff?
              </Link>
            ) : (
              <span>AUTHORIZED ACCESS ONLY</span>
            )}
          </div>
        </div>
      </div>
    </div>
    <LegalFooter />
    </div>
  );
}
